import axios from "axios";
import { authClient } from "../lib/auth-client";
import { hadSession, reportClientDiag } from "../lib/sessionDiagnostics";
import {
  getAppBuildInfo,
  getAuthToken,
  redirectToSignIn,
} from "../lib/platform";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Better Auth uses cookie-based authentication
// withCredentials: true ensures cookies are sent with each request
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Native (Capacitor) transport: the WebView origin can't hold cross-site
// cookies, so data calls carry the better-auth bearer token instead. No-op on
// web (getAuthToken resolves null off-native).
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Which binary sent this. Native only: a web bundle always deploys with the
  // API it was built against, so there is nothing to identify. These let an
  // incident be traced to a specific build server-side, and they are allowed
  // by the main CORS allowedHeaders — removing them there breaks every native
  // request at preflight.
  const buildInfo = await getAppBuildInfo();
  if (buildInfo) {
    config.headers["X-App-Build"] = String(buildInfo.build);
    config.headers["X-App-Platform"] = buildInfo.platform;
  }
  return config;
});

const SESSION_RECHECK_DELAY_MS = 500;
const BEACON_FLUSH_TIMEOUT_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// better-fetch resolves non-2xx responses as {data: null, error: {status}}
// rather than throwing, so a thrown error only covers network failures.
// Only a clean 200-null (no error) proves the session is gone — any HTTP
// error (5xx/429/edge block) is infrastructure, not a logout.
const fetchSessionState = async (): Promise<"alive" | "dead" | "unknown"> => {
  try {
    const { data, error } = await authClient.getSession({
      query: { disableCookieCache: true },
    });

    if (data?.user) return "alive";
    if (error == null) return "dead";
    return "unknown";
  } catch {
    return "unknown";
  }
};

let sessionCheck: Promise<boolean> | null = null;

// Before treating a data-call 401 as "logged out", confirm with better-auth
// that the session is really gone — a transient blip on one endpoint must not
// log the user out. Single shared promise so a burst of 401s (e.g. parallel
// dashboard queries) triggers exactly one check. disableCookieCache forces a
// DB-backed answer instead of trusting the 5-minute cookie cache.
const isSessionAlive = (): Promise<boolean> => {
  sessionCheck ??= (async () => {
    const first = await fetchSessionState();

    if (first === "alive") return true;
    if (first === "unknown") return true;

    // Clean "no session". On a browser that recently held a session this may
    // be the same cold-start race that caused the original 401 — give the
    // cookie layer a moment and confirm once before logging the user out.
    if (hadSession()) {
      await wait(SESSION_RECHECK_DELAY_MS);
      const second = await fetchSessionState();
      return second !== "dead";
    }

    return false;
  })().finally(() => {
    sessionCheck = null;
  });

  return sessionCheck;
};

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're not already on an auth, password reset, email verification, or public page
      const isAuthPage =
        window.location.pathname === "/sign-in" ||
        window.location.pathname === "/sign-up" ||
        window.location.pathname === "/forgot-password" ||
        window.location.pathname === "/reset-password" ||
        window.location.pathname === "/verify-email" ||
        window.location.pathname === "/terms" ||
        window.location.pathname === "/privacy-policy" ||
        window.location.pathname === "/support" ||
        window.location.pathname === "/unsubscribe" ||
        window.location.pathname === "/onboarding" ||
        window.location.pathname.startsWith("/auth/");

      if (!isAuthPage) {
        const url = error.config?.url ?? "<unknown>";
        const alive = await isSessionAlive();

        if (alive) {
          reportClientDiag("data-call-401-session-alive", { url });
        } else {
          // Give the beacon a moment to flush — the hard navigation below
          // would otherwise cancel it on some browsers, losing the one event
          // that documents the redirect.
          await Promise.race([
            reportClientDiag("data-call-401-redirect", {
              url,
              pathname: window.location.pathname,
            }),
            wait(BEACON_FLUSH_TIMEOUT_MS),
          ]);

          // Session expired or invalid, redirect to sign-in (on native this
          // also clears the stored bearer token first)
          void redirectToSignIn();
        }
      }
    }
    return Promise.reject(error);
  },
);
